import React, { useState } from 'react';
import { DatePicker, Input, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { withdrawInvoice } from '../api/invoiceWorkflowApi';
import type { WorkflowInvoiceDirection } from '../../../types/workflowInvoice';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { useFeedback } from '../../../utils/feedback';

// Owner 2026-10-19: any non-blank reason (no 10-character minimum).
const REASON_MIN = 1;
const REASON_MAX = 500;

interface InvoiceWithdrawModalProps {
  open: boolean;
  direction: WorkflowInvoiceDirection;
  invoiceId: string;
  number: string | null;
  /**
   * YYYY-MM of the invoice when a withdrawal there is allowed (backend
   * `withdrawalMonthSuggestion`, decision 2026-11-02): a mistaken invoice is
   * withdrawn in its own month.
   */
  suggestedMonth?: string | null;
  onClose: () => void;
  /** The invoice was withdrawn: reload what shows it. */
  onWithdrawn: () => void;
}

/**
 * WP2 withdrawal of an approved invoice (administrator, reason, month): the
 * invoice stays in history, what it recognized is reversed. One modal for the
 * invoice card („Wycofaj fakturę”) and the ZK card („Usuń fakturę”).
 */
export default function InvoiceWithdrawModal({ open, direction, invoiceId, number, suggestedMonth = null, onClose, onWithdrawn }: InvoiceWithdrawModalProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const feedback = useFeedback();
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;

  const withdraw = async () => {
    if (!reasonValid || !month) return;
    setBusy(true);
    try {
      await withdrawInvoice(direction, invoiceId, { reason: trimmedReason, recognitionMonth: month });
      feedback.success(t('invoices:workflow.corrections.withdrawn', { number: number || '' }));
      setReason('');
      onClose();
      onWithdrawn();
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.corrections.withdrawFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('invoices:workflow.corrections.withdrawTitle', { number: number || '' })}
      okText={t('invoices:workflow.corrections.withdrawConfirm')}
      okButtonProps={{ danger: true, disabled: !reasonValid || !month, loading: busy }}
      cancelText={t('common:cancel')}
      onOk={() => void withdraw()}
      onCancel={() => { if (!busy) onClose(); }}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Typography.Paragraph>{t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'withdrawEffectPurchase' : 'withdrawEffectSales'}`)}</Typography.Paragraph>
        <label>
          <span>{t('invoices:workflow.corrections.withdrawReason')}</span>
          <Input.TextArea aria-label={t('invoices:workflow.corrections.withdrawReason')} value={reason} maxLength={REASON_MAX}
            rows={3} onChange={(event) => setReason(event.target.value)} />
        </label>
        <label>
          <span>{t('invoices:workflow.corrections.withdrawMonth')}</span>
          <DatePicker picker="month" aria-label={t('invoices:workflow.corrections.withdrawMonth')} style={{ width: '100%' }}
            value={month ? dayjs(`${month}-01`) : null} onChange={(date) => setMonth(date?.format('YYYY-MM') ?? '')} />
        </label>
        {/* The default is the current month; withdrawing a mistaken invoice there leaves it in its own month's result. */}
        {suggestedMonth && month !== suggestedMonth && (
          <Typography.Text type="warning" data-testid="invoice-withdraw-month-hint">
            {t('invoices:workflow.corrections.withdrawMonthHint', { month: dayjs(`${suggestedMonth}-01`).format('MM.YYYY') })}{' '}
            <Typography.Link onClick={() => setMonth(suggestedMonth)}>
              {t('invoices:workflow.corrections.withdrawMonthUseInvoice', { month: dayjs(`${suggestedMonth}-01`).format('MM.YYYY') })}
            </Typography.Link>
          </Typography.Text>
        )}
      </Space>
    </Modal>
  );
}
